import React from 'react';
import { OptimizedImage } from '@/components/common/OptimizedImage';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, A11y, Autoplay } from 'swiper/modules';
import InteractivePopover from '@/components/common/InteractivePopover';
import InclusionBadge from '@/components/common/InclusionBadge';
import CurrencyDisplay from '@/components/common/CurrencyDisplay';
import { BackendCurrency } from '@/utils/currencyHelpers';
import 'swiper/css';
import 'swiper/css/pagination';

interface HighlightLocation {
  name?: string;
  city?: string;
  state?: string;
}

interface Highlight {
  _id?: string;
  title: string;
  description?: string;
  imageUrl?: string[];
  locations?: HighlightLocation;
  isIncluded?: boolean;
  adultPrice?: number;
  childPrice?: number;
}

interface HighlightCardProps {
  highlight: Highlight;
  index: number;
  backendCurrency?: BackendCurrency;
  expandedDescriptions: { [key: string]: boolean };
  shouldShowSeeMore: { [key: string]: boolean };
  onToggleDescription: (id: string) => void;
  onCheckOverflow: (element: HTMLParagraphElement, key: string) => void;
}

const HighlightCard: React.FC<HighlightCardProps> = ({
  highlight,
  index,
  backendCurrency,
  expandedDescriptions,
  shouldShowSeeMore,
  onToggleDescription,
  onCheckOverflow,
}) => {
  const highlightId = highlight._id || index;

  return (
    <InteractivePopover
      triggerClassName='cursor-pointer flex-shrink-0 w-[180px]'
      contentClassName='w-[320px] p-0 !rounded-[15px] !border-white border shadow-[0px_0px_5px_1px_rgba(0,0,0,0.15)] overflow-hidden'
      trigger={
        <>
          <div
            className='relative mb-2 overflow-hidden rounded-lg'
            style={{ width: '180px', height: '90px' }}
          >
            <div className='relative w-full h-full'>
              <OptimizedImage
                src={
                  highlight.imageUrl?.length > 0
                    ? highlight.imageUrl[0]
                    : '/assets/images/No-Image.jpg'
                }
                alt={highlight.title}
                fill
                style={{ objectFit: 'cover' }}
                className='rounded-lg'
                priority
                loading='eager'
                disableBlobCache={true}
              />
            </div>
          </div>
          <h4 className='text-[12px] font-normal text-black leading-[15px] capitalize mb-1'>
            {highlight.title}
          </h4>
          {highlight.isIncluded !== undefined && (
            <InclusionBadge
              isIncluded={highlight.isIncluded === true}
              variant='short'
              size='sm'
            />
          )}
        </>
      }
      content={
        <div className='bg-white overflow-hidden flex flex-col max-h-[420px] sm:max-h-[380px]'>
          {/* Image Carousel */}
          <div className='relative overflow-hidden flex-shrink-0'>
            {highlight.imageUrl?.length > 0 ? (
              <div className='w-full h-[72px] sm:h-[120px] relative overflow-hidden'>
                <Swiper
                  modules={[Pagination, A11y, Autoplay]}
                  slidesPerView={1}
                  pagination={{ clickable: true }}
                  autoplay={{
                    delay: 3000,
                    disableOnInteraction: false,
                  }}
                  className='w-full h-full'
                >
                  {highlight.imageUrl.map((src, idx) => (
                    <SwiperSlide key={`${src}-${idx}`}>
                      <div className='relative w-full h-full'>
                        <OptimizedImage
                          src={src || '/assets/images/No-Image.jpg'}
                          alt={`${highlight.title} ${idx + 1}`}
                          fill
                          style={{ objectFit: 'cover' }}
                          priority={idx === 0}
                          loading={idx === 0 ? 'eager' : 'lazy'}
                          disableBlobCache={true}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            ) : (
              <div className='w-full h-[72px] sm:h-[120px] relative overflow-hidden'>
                <OptimizedImage
                  src='/assets/images/No-Image.jpg'
                  alt={highlight.title}
                  fill
                  style={{ objectFit: 'cover' }}
                  priority
                  loading='eager'
                  disableBlobCache={true}
                />
              </div>
            )}
          </div>

          {/* Content */}
          <div className='px-3 py-2 overflow-y-auto flex-1 min-h-0'>
            {/* Title and location: same line on mobile, stacked on desktop */}
            <div className='flex flex-row flex-wrap items-center gap-x-2 gap-y-1 md:flex-col md:items-start mb-2'>
              <h3 className='text-[14px] font-semibold text-black leading-[17px] capitalize min-w-0'>
                {highlight.title}
              </h3>
              {highlight.locations && (
                <span className='inline-flex flex-shrink-0 items-center gap-1.5 px-2 py-1 text-[12px] font-normal text-darkgrey leading-[15px] bg-gray-100 rounded border border-gray-200'>
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 12 12'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M6 0C3.23858 0 1 2.23858 1 5C1 8.5 6 12 6 12C6 12 11 8.5 11 5C11 2.23858 8.76142 0 6 0ZM6 6.5C5.33696 6.5 4.70107 6.23661 4.23223 5.76777C3.76339 5.29893 3.5 4.66304 3.5 4C3.5 3.33696 3.76339 2.70107 4.23223 2.23223C4.70107 1.76339 5.33696 1.5 6 1.5C6.66304 1.5 7.29893 1.76339 7.76777 2.23223C8.23661 2.70107 8.5 3.33696 8.5 4C8.5 4.66304 8.23661 5.29893 7.76777 5.76777C7.29893 6.23661 6.66304 6.5 6 6.5Z'
                      fill='#7A7A7A'
                    />
                  </svg>
                  {[
                    highlight.locations.city,
                    highlight.locations.state,
                    highlight.locations.name,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              )}
            </div>

            {/* Description */}
            {highlight.description && (
              <div className='mt-2'>
                {(() => {
                  const isExpanded = expandedDescriptions[highlightId];
                  const shouldTruncate =
                    shouldShowSeeMore[highlightId] ?? false;

                  return (
                    <>
                      <p
                        ref={el => {
                          if (el) {
                            requestAnimationFrame(() => {
                              onCheckOverflow(el, String(highlightId));
                            });
                          }
                        }}
                        className={`text-[12px] font-normal text-darkgrey leading-[17px] ${
                          !isExpanded && shouldTruncate ? 'line-clamp-3' : ''
                        }`}
                      >
                        {highlight.description}
                      </p>
                      {shouldTruncate && (
                        <button
                          onClick={() =>
                            onToggleDescription(String(highlightId))
                          }
                          className='text-black font-medium mt-2 text-[11px] underline'
                        >
                          {isExpanded ? 'See less' : 'See more'}
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* Price & Inclusion Section */}
            {(highlight.adultPrice > 0 ||
              highlight.childPrice > 0 ||
              highlight.isIncluded !== undefined) && (
              <div className='mt-3 pt-3 border-t border-gray-200'>
                {/* Price Section */}
                {(highlight.adultPrice > 0 || highlight.childPrice > 0) && (
                  <div className='mb-2'>
                    <p className='text-[12px] font-medium text-black mb-2'>
                      Price:
                    </p>
                    <div className='flex gap-4'>
                      {highlight.adultPrice > 0 && (
                        <div className='flex flex-col'>
                          <span className='text-[10px] text-darkgrey mb-1'>
                            Adult
                          </span>
                          <span className='text-[14px] font-semibold text-black'>
                            <CurrencyDisplay
                              amount={highlight.adultPrice}
                              backendCurrency={backendCurrency}
                            />
                          </span>
                        </div>
                      )}
                      {highlight.childPrice > 0 && (
                        <div className='flex flex-col'>
                          <span className='text-[10px] text-darkgrey mb-1'>
                            Child
                          </span>
                          <span className='text-[14px] font-semibold text-black'>
                            <CurrencyDisplay
                              amount={highlight.childPrice}
                              backendCurrency={backendCurrency}
                            />
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Included/Not Included Section */}
                {highlight.isIncluded !== undefined && (
                  <div
                    className={`${
                      highlight.adultPrice > 0 || highlight.childPrice > 0
                        ? 'pt-2 border-t border-gray-100'
                        : ''
                    }`}
                  >
                    <InclusionBadge
                      isIncluded={highlight.isIncluded === true}
                      variant='long'
                      size='sm'
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      }
    />
  );
};

export default HighlightCard;
